<template>
  <div
    class="gs-app gs-refresh-controls gs-player-panel"
    :class="{ 'is-picker-section': hostsEscapingPopup }"
  >
    <gs-accordion :sections="sections" :value="open" @input="open = $event">
      <template v-slot:details>
        <details-section
          ref="details"
          :model="model"
          :categories="categories"
          v-on="sectionListeners"
          @categoryCreated="selectNewCategory($event)"
          @replaced="$emit('replaced')"
        />
      </template>

      <template v-slot:theme>
        <theme-section ref="theme" :model="model" v-on="sectionListeners" />
      </template>

      <template v-slot:thumbnail>
        <thumbnail-section ref="thumbnail" :model="model" v-on="sectionListeners" />
      </template>

      <template v-slot:size>
        <size-section ref="size" :model="model" v-on="sectionListeners" />
      </template>

      <template v-slot:autoplay>
        <autoplay-section ref="autoplay" :model="model" v-on="sectionListeners" />
      </template>

      <template v-slot:playbutton>
        <play-button-section ref="playbutton" :model="model" v-on="sectionListeners" />
      </template>

      <template v-slot:controls>
        <controls-section ref="controls" :model="model" v-on="sectionListeners" />
      </template>

      <template v-slot:end>
        <end-action-section ref="end" :model="model" v-on="sectionListeners" />
      </template>
    </gs-accordion>
  </div>
</template>

<script>
import Category from '@/models/Category';
import { applyPlayerDefaults } from './playerOptions';
import GsAccordion from '../GsAccordion.vue';
import DetailsSection from './DetailsSection.vue';
import ThemeSection from './ThemeSection.vue';
import ThumbnailSection from './ThumbnailSection.vue';
import SizeSection from './SizeSection.vue';
import AutoplaySection from './AutoplaySection.vue';
import PlayButtonSection from './PlayButtonSection.vue';
import ControlsSection from './ControlsSection.vue';
import EndActionSection from './EndActionSection.vue';

export default {
  name: 'PlayerPanel',

  // The shell hands every panel the same props; the ones this panel does not
  // declare would otherwise land on its root element.
  inheritAttrs: false,

  components: {
    GsAccordion,
    DetailsSection,
    ThemeSection,
    ThumbnailSection,
    SizeSection,
    AutoplaySection,
    PlayButtonSection,
    ControlsSection,
    EndActionSection,
  },

  props: {
    model: {
      type: Object,
      required: true,
    },
    // The shell's "the video is playable" flag. Declared so it stays a prop
    // rather than landing on this panel's root element.
    status: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      open: 'details',
      categories: [],
    };
  },

  computed: {
    sections() {
      return [
        { value: 'details', title: 'Details' },
        { value: 'theme', title: 'Theme & colours' },
        { value: 'thumbnail', title: 'Thumbnail & watermark' },
        { value: 'size', title: 'Size & layout' },
        { value: 'autoplay', title: 'Autoplay & start' },
        { value: 'playbutton', title: 'Play button' },
        { value: 'controls', title: 'Player controls' },
        { value: 'end', title: 'End of video' },
      ];
    },

    // Sections whose popup has to escape the accordion's clipped box. The
    // accordion gives a section no per-section hook, so the panel carries the
    // flag. Theme hosts the color picker; its HEX field is in the lower half
    // of that panel and was covered by the next section.
    hostsEscapingPopup() {
      return ['theme', 'autoplay', 'playbutton'].includes(this.open);
    },

    // Every section speaks the same two events; the shell owns both.
    sectionListeners() {
      return {
        updateModel: value => this.$emit('updateModel', value),
        forceRerender: () => this.$emit('forceRerender'),
      };
    },
  },

  async created() {
    this.applyDefaults();
    this.$emit('setIsLoading', true);
    await this.getCategories();
    this.$emit('setIsLoading', false);
  },

  methods: {
    // The shell fills these before it snapshots, so this is only a safety net
    // for a model that reached the panel another way. Anything it does fill is
    // reported so the shell can move those keys onto the saved baseline — the
    // user did not make these changes.
    applyDefaults() {
      const filled = applyPlayerDefaults(this.model, this.$set);
      if (filled.length) this.$emit('patch-snapshot', filled);
    },

    // Ported from step1.
    async getCategories() {
      const category = await Category.$get();
      this.categories = [];

      // Default category at top
      if (category?.[0]?.id !== 1) {
        const defaultIndex = category.findIndex(value => {
          return value.id === 1;
        });

        if (defaultIndex >= 0) {
          const defaultCategory = category[defaultIndex];
          category.splice(defaultIndex, 1);
          category.unshift(defaultCategory);
        }
      }

      // Set categories
      category.forEach(value => {
        this.categories.push(value);
      });
    },

    // Ported from step1's `saveCategory`: reload the list, then select the new
    // category. The modal closes itself in the section that opened it.
    async selectNewCategory(categoryId) {
      this.$emit('setIsLoading', true);
      await this.getCategories();
      if (categoryId) {
        this.model.category_id = categoryId;
        this.$emit('updateModel', this.model);
      }
      this.$emit('setIsLoading', false);
    },

    // The accordion mounts one section at a time, so validating them all means
    // walking them. The panel ends on the first section that has an error, with
    // that section's fields marked, and answers the shell with the combined
    // result the way each step used to.
    async validate() {
      const wasOpen = this.open;
      let firstInvalid = null;

      for (let i = 0; i < this.sections.length; i += 1) {
        const { value } = this.sections[i];
        this.open = value;
        // eslint-disable-next-line no-await-in-loop
        await this.$nextTick();
        const section = this.sectionAt(value);
        if (section && typeof section.validate === 'function' && section.validate() === false) {
          if (!firstInvalid) firstInvalid = value;
        }
      }

      this.open = firstInvalid || wasOpen;
      await this.$nextTick();
      if (firstInvalid) {
        // Re-opening remounts the section, which clears what `$touch` marked.
        const section = this.sectionAt(firstInvalid);
        if (section && typeof section.validate === 'function') section.validate();
      }

      const isValid = !firstInvalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    sectionAt(value) {
      const reference = this.$refs[value];
      return Array.isArray(reference) ? reference[0] : reference;
    },
  },
};
</script>

<style scoped>
.gs-player-panel {
  padding: 18px;
}

/*
  Relocated from step1.vue, which styled the shared image picker for every
  screen it appeared on and is deleted with this port. Tokenised, and scoped to
  this panel rather than the whole app.
*/
.gs-player-panel ::v-deep #gp-styleEditor-chooseImage {
  flex: auto;
  width: 300px;
  max-width: 100%;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-player-panel ::v-deep .imagePicker .pl-4 {
  text-align: left;
}

/*
  The accordion clips its sections so their corners stay round. Theme, autoplay
  and play button host a popup that has to escape the section box (design
  system §5.4: never clip a container that hosts a dropdown). Only the open
  one drops the clip, and it stacks above the sections under it so the color
  picker's HEX field stays reachable.
*/
.gs-player-panel.is-picker-section ::v-deep .gs-accordion__section.is-open {
  position: relative;
  z-index: 4;
  overflow: visible;
}

@media (max-width: 640px) {
  .gs-player-panel {
    padding: 14px;
  }
}
</style>
